import { useMemo } from "react"

// ** Third Party Components
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"

// ** UI Components
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../common/table"

// ** Utils
import { cn } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import { DataTableColumnHeader } from "../common/data-table/data-table-column-header"
import { Label } from "../common/label"

interface BillPrintTableProps {
  data: any
  isApproved: boolean
  status: any
}

const BillPrintTable = ({ data, isApproved, status }: BillPrintTableProps) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const columns = useMemo((): ColumnDef<any>[] => {
    const baseColumns: ColumnDef<any>[] = [
      {
        accessorKey: "number",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Section')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const itemNumber = item?.number
          return (
            <div className='flex gap-1'>
              <span className='text-xs sm:text-sm'>{itemNumber}</span>
            </div>
          )
        },
        enableSorting: false,
        size: 100,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Description')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const itemDescription = item?.description
          return (
            <div className='max-w-[200px] text-right text-xs leading-tight sm:max-w-xs sm:text-sm lg:min-w-[370px]'>
              {itemDescription}
            </div>
          )
        },
        size: 300,
        enableSorting: false,
      },
      {
        accessorKey: "units",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Unit')} className='text-center' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const itemUnit = item?.units
          return <div className='text-center text-xs sm:text-sm'>{itemUnit}</div>
        },
        enableSorting: false,
      },
      {
        accessorKey: "boq_cost",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Price per unit')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='min-w-fit text-center text-xs sm:text-sm'>
              <span className='flex items-center gap-1'>
                {formatCurrency(item?.bill?.discount_price, isHebrew)}
                <small className='text-[10px]'>
                  ({Number(((item?.bill?.item_discount ?? 0) * 100).toFixed(2))}%)
                </small>{" "}
              </span>{" "}
              {item?.is_item && (
                <div className='mt-1 flex items-center justify-center gap-1'>
                  <Label className='text-[10px]'>
                    {translate("Exclude Discount")}:{" "}
                    {item?.bill?.item_discount === 0 ? translate("True") : translate("False")}
                  </Label>
                </div>
              )}
            </div>
          )
        },
        size: 150,
        enableSorting: false,
      },
      {
        accessorKey: "amount",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Amount from estimate')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='max-w-xs text-right text-xs leading-tight sm:text-sm'>
              {item?.amount || ""}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
      },
      {
        accessorKey: "paid",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Amount')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row.original
          const accumulatedAmount = useMemo(() => {
            let totalAmount = 0
            if (["new", "pending"].includes(status) && item?.is_item) {
              totalAmount += (item?.bill?.amount || 0) + (Number(item?.bill?._boq_item?.paid) || 0)
            } else if (status === "approved" && item?.is_item) {
              totalAmount += (item?.bill?.approved_amount || 0) + (Number(item?.bill?._boq_item?.paid) || 0)
            } else if (status === "signed" && item?.is_item) {
              totalAmount += Number(item?.bill?._boq_item?.paid) || 0
            }

            return totalAmount
          }, [item, status])

          return (
            <div className='text-center text-xs sm:text-sm'>
              {formatCurrency(accumulatedAmount, isHebrew)}
            </div>
          )
        },
        size: 50,
        enableSorting: false,
      },
      {
        accessorKey: "current_bill",
        header: ({ column }) => <DataTableColumnHeader column={column} title={translate('Current Bill')} />,
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='flex min-w-[120px] flex-col text-center text-xs sm:min-w-fit sm:text-sm'>
              {isApproved ? item?.bill?.approved_amount : item?.bill?.amount}
            </div>
          )
        },
        size: 100,
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "current_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Current Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          const requestedSum = item?.bill?.requested_sum
          const approvedSum = item?.bill?.approved_sum
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              {item?.bill?.approved_sum
                ? formatCurrency(approvedSum, isHebrew)
                : formatCurrency(requestedSum, isHebrew)}
            </div>
          )
        },
        size: 200,
        enableSorting: false,
      },
      {
        accessorKey: "estimated_sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Boq sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          return (
            <div className='text-center text-xs font-bold sm:text-sm'>
              <span className=''>{formatCurrency(item?.real_sum, isHebrew)}</span>
            </div>
          )
        },
        enableSorting: false,
      },
      {
        accessorKey: "sum",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={translate('Accumulated Sum')} className='' />
        ),
        cell: ({ row }: any) => {
          const item = row?.original
          const accumulatedSum = useMemo(() => {
            let totalAmount = 0
            if (["new", "pending"].includes(status) && item?.is_item) {
              totalAmount += (item?.bill?.requested_sum || 0) + (Number(item?.bill?._boq_item?.paid_sum) || 0)
            } else if (status === "approved" && item?.is_item) {
              totalAmount += (item?.bill?.approved_sum || 0) + (Number(item?.bill?._boq_item?.paid_sum) || 0)
            } else if (status === "signed" && item?.is_item) {
              totalAmount += Number(item?.bill?._boq_item?.paid_sum) || 0
            }

            return totalAmount
          }, [status, item])
          return (
            <div className='text-center text-xs sm:text-sm'>
              {formatCurrency(accumulatedSum, isHebrew)}
            </div>
          )
        },
        enableSorting: false,
      },
    ]

    return baseColumns
  }, [isHebrew, isApproved, status, translate])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className='overflow-x-auto rounded-md border'>
      <Table className='text-xs sm:text-sm'>
        <TableHeader className='sticky top-0 z-10 bg-gray-50'>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className='border-b bg-gray-50'>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    "border-gray-200 px-1 py-2 text-center text-gray-900 sm:px-2 sm:py-3",
                    isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                  )}
                  style={{ width: header.getSize() }}
                >
                  {header.isPlaceholder ? null : (
                    <div className='flex items-center justify-center'>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row, index) => {
            const rowData = row.original
            const isParent = rowData.hasChildren
            return (
              <TableRow
                key={row.id}
                className={cn(
                  `border-b hover:bg-gray-50 ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-25"
                  } ${isParent ? "font-medium" : ""}`,
                  rowData?.bill?.amount && "border-y border-primary bg-primary/5",
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    className={cn(
                      "border-gray-200 px-1 py-1.5 sm:px-2 sm:py-2",
                      isHebrew ? "border-r first:border-r-0" : "border-l first:border-l-0",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

export default BillPrintTable
